import { formatMoney } from '@awpmarine/core/money';

import { languageTag } from '@/lib/locale';
import { careers } from '@/lib/source';
import { m } from '@/paraglide/messages';
import { type MessageFunction, getLocale } from '@/paraglide/runtime';

export type Role = ReturnType<typeof careers.getPages>[number];
export type Pay = NonNullable<Role['data']['pay']>;

const day = 24 * 60 * 60 * 1000;

/** Roles in the content: drafts only in development. */
export function publishedRoles() {
  return careers.getPages(getLocale()).filter((role) => import.meta.env.DEV || !role.data.draft);
}

/** One role by slug, open or not. Whether it still shows is the route loader's call. */
export function findRole(slug: string) {
  return publishedRoles().find((role) => role.slugs[0] === slug);
}

/** The end of the closing day, when the role stops taking applications. */
export function closingTime(role: Role) {
  return Date.parse(role.data.closes) + day;
}

export function isOpen(role: Role) {
  return !role.data.filled && Date.now() < closingTime(role);
}

/** Roles still taking applications, newest first. */
export function openRoles() {
  return publishedRoles()
    .filter(isOpen)
    .sort((a, b) => Date.parse(b.data.posted) - Date.parse(a.data.posted));
}

const payPer: Record<Pay['per'], (inputs: { amount: string }) => string> = {
  hour: m.careers_pay_per_hour,
  day: m.careers_pay_per_day,
  week: m.careers_pay_per_week,
  month: m.careers_pay_per_month,
  year: m.careers_pay_per_year,
};

/** "£450 per day", or "£45,000 to £55,000 per year" for a range. */
export function formatPay({ amount, currency, per }: Pay) {
  // Pay is written in whole units, which is scale 0.
  const money = (value: number) =>
    formatMoney({ currency, amount: value, scale: 0 }, { locale: languageTag(), whole: true });
  const value =
    typeof amount === 'number' ? money(amount) : m.careers_pay_range({ min: money(amount[0]), max: money(amount[1]) });
  return payPer[per]({ amount: value });
}

const roleTypes: Record<Role['data']['type'], MessageFunction> = {
  'Freelance': m.careers_type_freelance,
  'Full time': m.careers_type_full_time,
  'Part time': m.careers_type_part_time,
  'Contract': m.careers_type_contract,
};

/** A role's type as the page's locale reads it; the front matter keeps the English value. */
export function formatRoleType(type: Role['data']['type']) {
  return roleTypes[type]();
}

/** What the careers list shows of a role: plain data, so a route loader can hand it over. */
export type RoleSummary = {
  slug: string;
  title: string;
  type: string;
  location: string;
  pay?: string;
  posted: string;
  closes: string;
};

export function openRoleSummaries(): RoleSummary[] {
  return openRoles().map((role) => ({
    slug: role.slugs[0]!,
    title: role.data.title,
    type: formatRoleType(role.data.type),
    location: role.data.location,
    pay: role.data.pay && formatPay(role.data.pay),
    posted: role.data.posted,
    closes: role.data.closes,
  }));
}
